/**
 * Logo Marquee Animation
 * Simplified version for logo scroller with CSS-based animation
 */

export default class LogoMarquee {
    constructor() {
        this.containers = document.querySelectorAll('.marquee-container');
        this.init();
    }

    init() {
        if (this.containers.length === 0) return;

        this.containers.forEach(container => {
            this.setupMarquee(container);
        });

        // Handle resize
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    setupMarquee(container) {
        const wrapper = container.querySelector('.flex.relative.w-max');
        const originalContent = container.querySelector('.marquee-original');

        if (!wrapper || !originalContent) return;

        // Get content width for animation
        const contentWidth = originalContent.offsetWidth;

        // Create duplicates for seamless loop
        const duplicationsNeeded = Math.max(3, Math.ceil(window.innerWidth / contentWidth * 2));

        // Remove existing duplicates
        const existingDuplicates = wrapper.querySelectorAll('.marquee-duplicate');
        existingDuplicates.forEach(el => el.remove());

        // Create new duplicates
        for (let i = 0; i < duplicationsNeeded; i++) {
            const clone = originalContent.cloneNode(true);
            clone.classList.add('marquee-duplicate');
            clone.classList.remove('marquee-original');
            wrapper.appendChild(clone);
        }

        // Apply CSS animation
        this.applyAnimation(wrapper, contentWidth);

        // Fade in content
        setTimeout(() => {
            const allContent = wrapper.querySelectorAll('.marquee-original, .marquee-duplicate');
            allContent.forEach(el => {
                el.style.opacity = '1';
                el.style.transition = 'opacity 0.8s ease';
            });
        }, 100);
    }

    applyAnimation(wrapper, contentWidth) {
        // Calculate animation duration based on content width
        const duration = Math.max(20, contentWidth / 50); // Adjust speed as needed

        // Apply CSS animation
        wrapper.style.animation = `marqueeScroll ${duration}s linear infinite`;

        // Add CSS keyframes if not already present
        this.addKeyframes(contentWidth);
    }

    addKeyframes(contentWidth) {
        const styleId = 'logo-marquee-keyframes';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        styleElement.textContent = `
            @keyframes marqueeScroll {
                0% { transform: translateX(0); }
                100% { transform: translateX(-${contentWidth}px); }
            }

            .marquee-original,
            .marquee-duplicate {
                opacity: 0;
                transition: opacity 0.8s ease;
            }

            @media (prefers-reduced-motion: reduce) {
                .marquee-container .flex.relative.w-max {
                    animation-play-state: paused !important;
                    transform: translateX(0) !important;
                }
            }
        `;
    }

    handleResize() {
        // Debounce resize
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.containers.forEach(container => {
                this.setupMarquee(container);
            });
        }, 250);
    }

    destroy() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        
        // Remove keyframes
        const styleElement = document.getElementById('logo-marquee-keyframes');
        if (styleElement) {
            styleElement.remove();
        }

        // Reset containers
        this.containers.forEach(container => {
            const wrapper = container.querySelector('.flex.relative.w-max');
            if (wrapper) {
                wrapper.style.animation = '';
                const duplicates = wrapper.querySelectorAll('.marquee-duplicate');
                duplicates.forEach(el => el.remove());
            }
        });
    }
}
